import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { postMultipart, put, get, post } from 'api/fetch';

export const updateAmbassadorPaymentDetailsWise = createAsyncThunk(
  'global/AmbassadorDashboard/updateAmbassadorPaymentDetailsWise',
  async (recipientAccount, { getState, rejectWithValue }) => {
    const state = getState();
    const { userID } = state.app.user;

    try {
      const response = await put(
        `/ambassador/${userID}/payment-details/wise`,
        recipientAccount,
        {
          isNIOrchestrator: true
        }
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initState = {
  company: null,
  isLoading: false,
  wiseError: null,
  error: null,
  success: false,
  editInitialized: false,
  wiseDynamicForm: null,
  quoteId: null,
  wiseCurrencies: null,
  recipientId: null
};

const slice = createSlice({
  name: 'global/AmbassadorUpdate',
  initialState: initState,
  extraReducers: {
    [updateAmbassadorPaymentDetailsWise.pending]: (state) => {
      state.isLoading = true;
      state.wiseError = null;
      state.recipientId = null;
      state.success = false;
    },
    [updateAmbassadorPaymentDetailsWise.fulfilled]: (state, { payload }) => {
      state.recipientId = payload.wiseRecipientId;
      state.isLoading = false;
      state.wiseError = null;
      state.success = true;
    },
    [updateAmbassadorPaymentDetailsWise.rejected]: (state, { payload }) => {
      state.isLoading = false;
      state.recipientId = null;
      state.wiseError = payload;
      state.success = false;
    }
  },
  reducers: {
    updateAmbassador: (state) => {
      state.isLoading = true;
      state.error = null;
      state.success = false;
    },
    updateAmbassadorSuccess: (state) => {
      state.error = null;
      state.success = true;
      state.isLoading = false;
    },
    updateAmbassadorFailure: (state, { payload }) => {
      state.isLoading = false;
      state.error = payload;
      state.success = false;
    },
    updateCompany: (state, { payload }) => {
      state.company = payload;
      state.editInitialized = false;
    },
    createAgreementDocument: (state) => {
      state.isLoading = true;
      state.success = false;
    },
    clearError: (state) => {
      state.error = null;
      state.wiseError = null;
    },
    clearState: (state) => {
      state.company = null;
      state.isLoading = false;
      state.error = null;
      state.success = false;
    },
    setPaymentDetailsUpdateInitialized: (state, { payload: editInitialized }) => {
      state.editInitialized = editInitialized;
    },
    fetchWiseDynamicForm: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    fetchWiseDynamicFormSuccess: (state, { payload }) => {
      state.wiseDynamicForm = payload.entries;
      state.quoteId = payload.quoteId;
      state.isLoading = false;
      state.error = null;
    },
    fetchWiseDynamicFormFailure: (state, { payload }) => {
      state.isLoading = false;
      state.error = payload;
    },
    refreshWiseDynamicForm: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    refreshWiseDynamicFormSuccess: (state, { payload }) => {
      state.wiseDynamicForm = payload.entries;
      state.quoteId = payload.quoteId;
      state.isLoading = false;
      state.error = null;
    },
    refreshWiseDynamicFormFailure: (state, { payload }) => {
      state.isLoading = false;
      state.error = payload;
    },
    fetchWiseCurrencies: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    fetchWiseCurrenciesSuccess: (state, { payload }) => {
      state.wiseCurrencies = payload;
      state.isLoading = false;
      state.error = null;
    },
    fetchWiseCurrenciesFailure: (state, { payload }) => {
      state.isLoading = false;
      state.error = payload;
    }
  }
});

export default slice.reducer;

export const { setPaymentDetailsUpdateInitialized, ...actions } = slice.actions;

export const fetchWiseDynamicForm = (currency) => async (dispatch) => {
  dispatch(actions.fetchWiseDynamicForm());

  try {
    const response = await get(
      `/wise/dynamic-form/account-requirements?currency=${currency}`,
      {
        isNIOrchestrator: true
      }
    );
    dispatch(actions.fetchWiseDynamicFormSuccess(response));
  } catch (error) {
    dispatch(actions.fetchWiseDynamicFormFailure(error));
  }
};

export const refreshWiseDynamicForm = (accountRequirements) => async (dispatch) => {
  dispatch(actions.refreshWiseDynamicForm());

  try {
    const response = await post(
      `/wise/dynamic-form/account-requirements`,
      accountRequirements,
      {
        isNIOrchestrator: true
      }
    );
    dispatch(actions.refreshWiseDynamicFormSuccess(response));
  } catch (error) {
    dispatch(actions.refreshWiseDynamicFormFailure(error));
  }
};

export const fetchWiseCurrencies = () => async (dispatch) => {
  dispatch(actions.fetchWiseCurrencies());

  try {
    const response = await get('/wise/dynamic-form/currencies', {
      isNIOrchestrator: true
    });
    dispatch(actions.fetchWiseCurrenciesSuccess(response));
  } catch (error) {
    dispatch(actions.fetchWiseCurrenciesFailure(error));
  }
};

export const updateAmbassador = (ambassador) => async (dispatch) => {
  dispatch(actions.updateAmbassador());
  try {
    await postMultipart('/affiliate/update', ambassador);
    await dispatch(actions.updateAmbassadorSuccess());
  } catch (error) {
    dispatch(actions.updateAmbassadorFailure(error));
  }
};

export const updateAmbassadorDetails = (userID, ambassador) => async (dispatch) => {
  dispatch(actions.updateAmbassador());
  try {
    await put(`/affiliate/${userID}`, ambassador);
    dispatch(actions.updateAmbassadorSuccess());
  } catch (error) {
    dispatch(actions.updateAmbassadorFailure(error));
  }
};

export const updateCompany = (ambassador) => async (dispatch) => {
  dispatch(actions.updateCompany(ambassador));
};

export const createAgreementDocument = () => async (dispatch) => {
  dispatch(actions.createAgreementDocument());
};

export const clearError = () => (dispatch) => {
  dispatch(actions.clearError());
};

export const clearState = () => (dispatch) => {
  dispatch(actions.clearState());
};
